import { useEffect, useCallback, useState } from 'react'

interface PWAOptimizations {
  isPWA: boolean
  isMobile: boolean
  viewportHeight: number
  supportsPassiveEvents: boolean
}

export function usePWAOptimizations(): PWAOptimizations {
  const [isPWA, setIsPWA] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [viewportHeight, setViewportHeight] = useState(0)
  const [supportsPassiveEvents, setSupportsPassiveEvents] = useState(false)

  // Detect PWA mode
  const detectPWA = useCallback(() => {
    const isPWAMode = 
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://') // Android TWA
    
    setIsPWA(isPWAMode)
  }, [])

  // Check for mobile device
  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth < 768)
  }, [])

  // Update viewport height for mobile browsers
  const updateViewportHeight = useCallback(() => {
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
    setViewportHeight(window.innerHeight)
  }, [])

  // Detect passive event support
  const detectPassiveEvents = useCallback(() => {
    let supportsPassive = false
    try {
      const opts = Object.defineProperty({}, 'passive', {
        get() {
          supportsPassive = true
          return false
        }
      })
      window.addEventListener('test', null as any, opts)
      window.removeEventListener('test', null as any, opts)
    } catch (e) {
      // Passive events not supported
    }
    setSupportsPassiveEvents(supportsPassive)
  }, [])

  // Setup viewport height CSS variable
  const setupViewportHeight = useCallback(() => {
    // Initial setup
    updateViewportHeight()
    
    // Handle resize events
    const handleResize = () => {
      checkMobile()
      updateViewportHeight()
    }

    // Use passive listeners if supported
    const eventOptions = supportsPassiveEvents ? { passive: true } : false

    window.addEventListener('resize', handleResize, eventOptions)
    window.addEventListener('orientationchange', updateViewportHeight, eventOptions)
    
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', updateViewportHeight)
    }
  }, [checkMobile, updateViewportHeight, supportsPassiveEvents])

  // Optimize scrolling performance
  const optimizeScrolling = useCallback(() => {
    // Add smooth scrolling CSS class to root
    document.documentElement.classList.add('smooth-scroll')
    
    // Prevent overscroll bounce on iOS in PWA mode
    if (isPWA && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
      document.body.style.overscrollBehavior = 'none'
      document.body.style.webkitOverscrollBehavior = 'none'
    }

    return () => {
      document.documentElement.classList.remove('smooth-scroll')
    }
  }, [isPWA])

  // Setup touch optimizations
  const setupTouchOptimizations = useCallback(() => {
    if (isMobile) {
      // Add touch optimization classes
      document.body.classList.add('touch-optimized')
      
      // Prevent zoom on double tap (PWA specific)
      if (isPWA) {
        let lastTouchEnd = 0
        const preventDoubleTabZoom = (event: TouchEvent) => {
          const now = Date.now()
          if (now - lastTouchEnd <= 300) {
            event.preventDefault()
          }
          lastTouchEnd = now
        }
        
        document.addEventListener('touchend', preventDoubleTabZoom, false)
        
        return () => {
          document.removeEventListener('touchend', preventDoubleTabZoom, false)
          document.body.classList.remove('touch-optimized')
        }
      }
    }
  }, [isMobile, isPWA])

  // Setup performance optimizations
  const setupPerformanceOptimizations = useCallback(() => {
    // Enable hardware acceleration globally
    document.documentElement.style.transform = 'translateZ(0)'
    
    // Optimize font rendering
    document.documentElement.style.webkitFontSmoothing = 'antialiased'
    document.documentElement.style.mozOsxFontSmoothing = 'grayscale'
    
    // Setup requestIdleCallback for non-critical operations
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        // Preload critical resources
        const criticalImages = document.querySelectorAll('img[data-critical]')
        criticalImages.forEach(img => {
          if (img instanceof HTMLImageElement && img.dataset.src) {
            img.src = img.dataset.src
          }
        })
      })
    }

    return () => {
      document.documentElement.style.transform = ''
    }
  }, [])

  // Main initialization effect
  useEffect(() => {
    detectPWA()
    checkMobile()
    detectPassiveEvents()
    
    // Listen for PWA mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)')
    mediaQuery.addEventListener('change', detectPWA)
    
    return () => {
      mediaQuery.removeEventListener('change', detectPWA)
    }
  }, [detectPWA, checkMobile, detectPassiveEvents])

  // Setup optimizations when dependencies change
  useEffect(() => {
    const cleanupViewport = setupViewportHeight()
    const cleanupScrolling = optimizeScrolling()
    const cleanupTouch = setupTouchOptimizations()
    const cleanupPerformance = setupPerformanceOptimizations()
    
    return () => {
      cleanupViewport?.()
      cleanupScrolling?.()
      cleanupTouch?.()
      cleanupPerformance?.()
    }
  }, [setupViewportHeight, optimizeScrolling, setupTouchOptimizations, setupPerformanceOptimizations])

  // PWA-specific meta tag updates
  useEffect(() => {
    if (isPWA) {
      // Update theme color for PWA
      const themeColorMeta = document.querySelector('meta[name="theme-color"]')
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#CC0000')
      }

      // Disable user scalability in PWA mode
      const viewportMeta = document.querySelector('meta[name="viewport"]')
      if (viewportMeta) {
        const currentContent = viewportMeta.getAttribute('content') || ''
        if (!currentContent.includes('user-scalable=no')) {
          viewportMeta.setAttribute(
            'content', 
            `${currentContent}, user-scalable=no`
          )
        }
      }

      // Add PWA class to body
      document.body.classList.add('pwa-mode')

      return () => {
        document.body.classList.remove('pwa-mode')
      }
    }
  }, [isPWA])

  return {
    isPWA,
    isMobile,
    viewportHeight,
    supportsPassiveEvents
  }
}